import React, { useMemo, useState } from 'react';
import clsx from 'clsx';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { ForecastListItem } from '@futrli-sdk/predict';

import Form from 'carbon-react/lib/components/form';
import Button from 'carbon-react/lib/components/button';
import Textbox from 'carbon-react/lib/components/textbox';
import Icon from 'carbon-react/lib/components/icon';
import { Option, Select } from 'carbon-react/lib/components/select';
import { ReportingSidebar } from '../reporting-sidebar/reporting-sidebar';

import { useReportBuilderContext } from '../../report-builder.context';
import { useI18nConstants } from '../../use-i18n-constants';

import styles from './map-template-data-modal.scss';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (origDS: string[], mapDS: string[]) => void;
}

export const MapDatasetsFutrliTemplateModal = ({ isOpen, onClose, onSave }: Props) => {
  const intl = useIntl();
  const { dummyTemplateForecasts } = useI18nConstants();
  const { datasetsToMap, forecasts } = useReportBuilderContext();
  const [dsMappedTo, setDSMappedTo] = useState<string[]>([]);

  const dsObjects: ForecastListItem[] = useMemo(
    () => datasetsToMap.map(d => dummyTemplateForecasts.find(p => p.id === d) as ForecastListItem).filter(e => !!e),
    [datasetsToMap, dummyTemplateForecasts]
  );

  const handleSaveDatasetsMapping = () => {
    const origDS: string[] = [];
    const mapDS: string[] = [];
    dsObjects.forEach((ds, i) => {
      if (ds && dsMappedTo[i]) {
        origDS.push(ds.id);
        mapDS.push(dsMappedTo[i]);
      }
    });
    onSave(origDS, mapDS);
    onClose();
  };

  const handleDSMapToChange = (dsRef: string, index: number) => {
    const dsMappedToCopy = [...dsMappedTo];
    dsMappedToCopy[index] = dsRef;
    setDSMappedTo(dsMappedToCopy);
  };

  return (
    <ReportingSidebar
      aria-label={intl.formatMessage({
        id: 'reporting.map_organisation_to_template',
        defaultMessage: 'Map organisation data to template data'
      })}
      title={intl.formatMessage({
        id: 'reporting.map_organisation_to_template',
        defaultMessage: 'Map organisation data to template data'
      })}
      open={isOpen}
      onCancel={onClose}
      sidebarProps={{
        size: 'extra-large'
      }}
    >
      <Form
        stickyFooter
        rightSideButtons={
          <>
            <Button onClick={onClose} buttonType="secondary">
              <FormattedMessage id="reporting.close_without_maping" defaultMessage="Close without mapping" />
            </Button>
            <Button onClick={handleSaveDatasetsMapping} buttonType="primary">
              <FormattedMessage id="common.save" defaultMessage="Save" />
            </Button>
          </>
        }
        onSubmit={() => null}
      >
        <p>
          <FormattedMessage
            id="reporting.account_mapping_description"
            defaultMessage="Account mappings need to happen once only. They are saved for use in further report templates. If you do not
          map, widgets <u>may need to be deleted</u> from your report."
            values={{ u: chunks => <u>{chunks}</u> }}
          />
        </p>
        <div className={clsx(styles.mapModalContent)}>
          <div>
            <div className={styles.mappingSection}>
              <div className={styles.leftSide}>
                <h3>
                  <FormattedMessage id="reporting.template_dataset_in_use" defaultMessage="Template dataset in use:" />
                </h3>
                {dsObjects.map(ds => (
                  <div key={`source-ds-${ds.id}`}>
                    <Textbox autoComplete="off" value={ds.name} onChange={() => undefined} disabled size="medium" />
                    <Icon type="arrow" />
                  </div>
                ))}
              </div>
              <div className={styles.rightSide}>
                <h3>
                  <FormattedMessage
                    id="reporting.template_dataset_to_map_to"
                    defaultMessage="Organisation dataset to map to:"
                  />
                </h3>
                {dsObjects.map((ds, i) => (
                  <Select
                    key={`target-${ds.id}`}
                    autoComplete="off"
                    value={dsMappedTo[i] || ''}
                    onChange={e => handleDSMapToChange(e.target.value, i)}
                  >
                    {forecasts.length > 0 && <Option value="" text="" />}
                    {forecasts.map(tds => (
                      <Option key={`option-${tds.id}`} value={tds.id} text={tds.name} />
                    ))}
                    {forecasts.length === 0 && (
                      <Option
                        key="no-dataset"
                        value=""
                        text={intl.formatMessage({
                          id: 'reporting.no_dataset_to_map_to',
                          defaultMessage: 'No dataset to map to'
                        })}
                      />
                    )}
                  </Select>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Form>
    </ReportingSidebar>
  );
};
